import React, { useContext, useEffect, useReducer } from 'react';
import { changeDishPositionReducer } from './changeDishPositionReducer';
import { ChangeDishPositionActionTypes } from './changeDishPositionActions';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	ChangeDishPositionContext,
	changeDishPositionInitialState,
	TTypeOfChangeDishPositionModalWindow,
} from './changeDishPositionContext';
import {
	IItemForGetShortInfo,
	IDishPositionItemV2,
	IGetDishPositionListV2,
	IGetDishPositionListArgumentsV2,
} from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getDishPositionListV2, getDishPhotos } from 'backend/dishes';
import { createAppError } from 'utils/helpers';

export const ChangeDishPositionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(changeDishPositionReducer, changeDishPositionInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const { response, loading, refetch } = useFetchData<IGetDishPositionListV2, IGetDishPositionListArgumentsV2>(
		state.IDSelectedSubCategory ? getDishPositionListV2 : null,
		{
			restaurantId: state.IDSelectedRestaurant || '',
			subCategoryId: state.IDSelectedSubCategory || '',
		},
	);

	useEffect(() => {
		response &&
			dispatch({
				type: ChangeDishPositionActionTypes.SET_POSITIONS_DATA,
				payload: {
					positionsData: response.data.dishes,
				},
			});
	}, [response]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ChangeDishPositionActionTypes.SET_POSITIONS_DATA,
				payload: {
					positionsData: response.data.dishes,
				},
			});

			const getDishesPreSignedUrl = async (dishIds: string[]) => {
				try {
					const dishPhotosResponse = await getDishPhotos({ ids: dishIds, size: 1 });
					const updatedDishes = response.data.dishes.reduce((acc: IDishPositionItemV2[], item) => {
						const dishPhotoFile = dishPhotosResponse.data.files.find(
							(photoFile) => photoFile.id === item.id,
						);
						acc.push({
							...item,
							preSignedUrl: dishPhotoFile ? dishPhotoFile.preSignedUrl : null,
						});
						return acc;
					}, []);

					dispatch({
						type: ChangeDishPositionActionTypes.SET_POSITIONS_DATA,
						payload: {
							positionsData: updatedDishes,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const dishIds = response.data.dishes.map((dish) => dish.id);
			getDishesPreSignedUrl(dishIds);
		}
	}, [response]);

	useEffect(() => {
		refetch();
	}, [state.IDSelectedSubCategory]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	};

	const setIDCategory = (value: Nullable<string>) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	};

	const setIDSubCategory = (value: Nullable<string>) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ID_SUB_CATEGORY,
			payload: {
				IDSelectedSubCategory: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ChangeDishPositionActionTypes.RESET_SELECTED_CONTROLS });

	const setPositionsData = (value: IDishPositionItemV2[]) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfChangeDishPositionModalWindow) => {
		dispatch({
			type: ChangeDishPositionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setVisibleAdditionalRestaurantsModal = (value: boolean) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_VISIBLE_ADDITIONAL_RESTAURANTS_MODAL,
			payload: {
				isVisibleAdditionalRestaurantsModal: value,
				isApplyToOne: value ? false : state.isApplyToOne,
				selectedAdditionalRestaurants: value ? [] : state.selectedAdditionalRestaurants,
			},
		});
	};

	const toggleApplyToOne = () => {
		dispatch({
			type: ChangeDishPositionActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: string[]) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				selectedAdditionalRestaurants: value,
			},
		});
	};

	const setFetchedRestaurants = (value: IItemForGetShortInfo[]) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	};

	return (
		<ChangeDishPositionContext.Provider
			value={{
				setIDCustomer,
				setIDRestaurant,
				setIDCategory,
				setIDSubCategory,
				resetSelectedControls,
				setPositionsData,
				changeTypeOfModalWindow,
				setVisibleAdditionalRestaurantsModal,
				toggleApplyToOne,
				setAdditionalRestaurants,
				setFetchedRestaurants,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedCategory: state.IDSelectedCategory,
				IDSelectedSubCategory: state.IDSelectedSubCategory,
				positionsData: state.positionsData,
				typeOfModalWindow: state.typeOfModalWindow,
				isVisibleAdditionalRestaurantsModal: state.isVisibleAdditionalRestaurantsModal,
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				fetchedRestaurants: state.fetchedRestaurants,
			}}>
			{children}
		</ChangeDishPositionContext.Provider>
	);
};
